/**
 * Frontend configuration
 */

export const config = {
  // API Configuration
  api: {
    baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5080/api',
    wsURL: import.meta.env.VITE_WS_URL || 'http://localhost:5002',
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000
  },

  // Localization
  locale: {
    default: import.meta.env.VITE_DEFAULT_LOCALE || 'ro',
    supported: ['ro', 'en']
  },

  // File Upload
  upload: {
    maxFileSize: 20 * 1024 * 1024, // 20MB
    maxZipSize: 50 * 1024 * 1024, // 50MB
    chunkSize: 5 * 1024 * 1024, // 5MB chunks for large files
    allowedExtensions: [
      // Documents
      '.pdf',
      '.doc',
      '.docx',
      '.xls',
      '.xlsx',
      '.ppt',
      '.pptx',
      '.odt',
      '.ods',
      '.odp',
      '.rtf',
      '.txt',
      '.csv',
      // Images
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.bmp',
      '.svg',
      '.webp',
      // Archives
      '.zip',
      '.rar',
      '.7z',
      '.tar',
      '.gz',
      // Data
      '.xml',
      '.json',
      '.sql'
    ],
    blockedExtensions: [
      '.exe',
      '.dll',
      '.msi',
      '.bat',
      '.cmd',
      '.com',
      '.sh',
      '.ps1',
      '.vbs',
      '.js',
      '.jar',
      '.apk',
      '.lnk',
      '.iso',
      '.img'
    ]
  },

  // WebSocket
  socket: {
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000
  },

  // Notifications
  notifications: {
    debounceTime: 600000, // 10 minutes in ms
    toastDuration: 5000, // 5 seconds
    maxToasts: 3
  },

  // Pagination
  pagination: {
    defaultPageSize: 20,
    pageSizeOptions: [10, 20, 50, 100],
    maxPageSize: 100
  },

  // UI Settings
  ui: {
    dateFormat: 'DD.MM.YYYY',
    timeFormat: 'HH:mm',
    dateTimeFormat: 'DD.MM.YYYY HH:mm',
    currency: 'RON',
    currencySymbol: 'lei'
  },

  // Feature Flags
  features: {
    chat: true,
    fileManager: true,
    notifications: true,
    bulkOperations: true,
    zipExtraction: true,
    messageEdit: true,
    messageDelete: true,
    fileNotes: true,
    audit: true
  },

  // Development
  dev: {
    enableLogging: import.meta.env.DEV,
    mockData: false,
    showDevTools: import.meta.env.DEV
  },

  // Sentry
  sentry: {
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    enabled: !!import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD,
    environment: import.meta.env.MODE,
    sendDefaultPii: true
  }
}

// API Endpoints
export const endpoints = {
  // Auth
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    profile: '/auth/profile'
  },

  // Users
  users: {
    list: '/users',
    get: (id: string) => `/users/${id}`,
    create: '/users',
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`
  },

  // Firms
  firms: {
    list: '/firms',
    get: (id: string) => `/firms/${id}`,
    create: '/firms',
    update: (id: string) => `/firms/${id}`,
    delete: (id: string) => `/firms/${id}`
  },

  // Clients
  clients: {
    list: '/clients',
    get: (id: string) => `/clients/${id}`,
    create: '/clients',
    update: (id: string) => `/clients/${id}`,
    delete: (id: string) => `/clients/${id}`
  },

  // Conversations
  conversations: {
    list: '/conversations',
    get: (id: string) => `/conversations/${id}`,
    messages: (id: string) => `/conversations/${id}/messages`,
    sendMessage: (id: string) => `/conversations/${id}/messages`
  },

  // Messages
  messages: {
    get: (id: string) => `/messages/${id}`,
    update: (id: string) => `/messages/${id}`,
    delete: (id: string) => `/messages/${id}`,
    edits: (id: string) => `/messages/${id}/edits`
  },

  // Reads
  reads: {
    update: '/reads'
  },

  // Files
  files: {
    list: '/files',
    get: (id: string) => `/files/${id}`,
    update: (id: string) => `/files/${id}`,
    delete: (id: string) => `/files/${id}`,
    notes: (id: string) => `/files/${id}/notes`,
    addNote: (id: string) => `/files/${id}/notes`,
    bulkMove: '/files/bulk/move',
    bulkProcess: '/files/bulk/process',
    bulkDelete: '/files/bulk/delete'
  },

  // Folders
  folders: {
    list: '/folders',
    get: (id: string) => `/folders/${id}`,
    create: '/folders',
    update: (id: string) => `/folders/${id}`,
    delete: (id: string) => `/folders/${id}`
  },

  // Uploads
  uploads: {
    presign: '/uploads/presign',
    finalize: '/uploads/finalize'
  },

  // Downloads
  downloads: {
    create: '/downloads'
  },

  // Notifications
  notifications: {
    list: '/notifications',
    get: (id: string) => `/notifications/${id}`,
    markRead: (id: string) => `/notifications/${id}/read`,
    test: '/notifications/test'
  },

  // Audit
  audit: {
    list: '/audit',
    get: (id: string) => `/audit/${id}`
  }
}

// WebSocket Events
export const socketEvents = {
  // Connection
  connect: 'connect',
  disconnect: 'disconnect',
  error: 'error',

  // Presence
  presenceUpdate: 'presence:update',

  // Typing
  typing: 'typing',

  // Messages
  messageNew: 'message:new',
  messageEdit: 'message:edit',
  messageDelete: 'message:delete',
  messageReadUpTo: 'message:read_up_to',

  // Files
  fileUploaded: 'file:uploaded',
  fileDeleted: 'file:deleted',
  fileMoved: 'file:moved',
  fileProcessed: 'file:processed',

  // Notifications
  notificationNew: 'notification:new'
}

export default config
